import React, { useState } from 'react'

interface SuggestionDropdownProps<T> {
  suggestions: T[]
  showSuggestions: boolean
  isLoading: boolean
  isExpanded: boolean
  selectedIndex: number
  suggestionsRef: React.RefObject<HTMLDivElement>
  onSuggestionClick: (suggestion: T) => void
  onToggleExpanded: (e: React.MouseEvent) => void
  renderSuggestion: (suggestion: T) => React.ReactNode
  fontClass: string | undefined
  formatMessage: (
    descriptor: { id: string; defaultMessage: string },
    values?: any
  ) => string
  ariaLabel: string
}

export function SuggestionDropdown<T>({
  suggestions,
  showSuggestions,
  isLoading,
  isExpanded,
  selectedIndex,
  suggestionsRef,
  onSuggestionClick,
  onToggleExpanded,
  renderSuggestion,
  fontClass,
  formatMessage,
  ariaLabel
}: SuggestionDropdownProps<T>) {
  const [hoveredIndex, setHoveredIndex] = useState<number>(-1)

  if (!showSuggestions || suggestions.length === 0) return null

  const initialVisibleCount = 5

  return (
    <div
      ref={suggestionsRef}
      className={`${fontClass} absolute z-50 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg`}
      role='listbox'
      aria-label={ariaLabel}
    >
      {isLoading && (
        <div className={`${fontClass} px-4 py-2 text-sm text-gray-500`}>
          {formatMessage({ id: 'loading', defaultMessage: 'Loading...' })}
        </div>
      )}
      {!isLoading && (
        <>
          <ul className='m-0 max-h-60 list-none overflow-y-auto p-0'>
            {/* Show first 5 suggestions by default */}
            {suggestions
              .slice(0, isExpanded ? suggestions.length : initialVisibleCount)
              .map((suggestion, index) => {
                const isSelected = selectedIndex === index
                const isHovered = hoveredIndex === index
                const isHighlighted = isSelected || isHovered

                return (
                  <li
                    key={`suggestion-${index}`}
                    className={`cursor-pointer px-4 py-2 text-sm transition-all duration-150 ${
                      isHighlighted
                        ? 'border-l-4 border-blue-500 bg-blue-100 font-semibold'
                        : 'bg-white'
                    }`}
                    onClick={() => onSuggestionClick(suggestion)}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(-1)}
                    role='option'
                    aria-selected={selectedIndex === index}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onSuggestionClick(suggestion)
                      }
                    }}
                  >
                    {renderSuggestion(suggestion)}
                  </li>
                )
              })}
          </ul>

          {/* Show expand/collapse button if there are more than initialVisibleCount suggestions */}
          {suggestions.length > initialVisibleCount && (
            <div
              className='cursor-pointer border-t border-gray-200 px-4 py-2 text-center text-sm text-blue-600 transition-colors duration-150 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500'
              onClick={onToggleExpanded}
              onMouseDown={(e) => e.preventDefault()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onToggleExpanded(e as any)
                }
              }}
              tabIndex={0}
              role='button'
              aria-expanded={isExpanded}
            >
              {isExpanded
                ? formatMessage({
                    id: 'msp.address_suggestions.show_less',
                    defaultMessage: 'Show less'
                  })
                : formatMessage(
                    {
                      id: 'msp.address_suggestions.show_more',
                      defaultMessage: 'Show {count} more'
                    },
                    { count: suggestions.length - initialVisibleCount }
                  )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
